import { useId, useState } from 'react';
import type { GameSave, GameServices } from '@/app/services';
import { useI18n } from '@/ui/i18n/I18nProvider';
import styles from './SaveFileActions.module.css';

type SaveFileResult = ReturnType<GameServices['readSaveFile']>;

/**
 * "Nhập file lưu" (content-schemas §11.7): pick a file, read it without touching anything, show what is in it
 * (xu, sao, số món) and only replace the current save once the player confirms. Reloads after importing.
 */
export function ImportSaveButton({
  read,
  apply,
}: {
  read: (text: string) => SaveFileResult;
  apply: (save: GameSave) => Promise<void>;
}) {
  const { t } = useI18n();
  const [picked, setPicked] = useState<SaveFileResult | null>(null);
  const [busy, setBusy] = useState(false);
  const error =
    picked && !picked.ok
      ? t(
          picked.reason === 'future'
            ? 'ui.settings.import_future'
            : picked.reason === 'too_big'
              ? 'ui.settings.import_too_big'
              : 'ui.settings.import_invalid',
        )
      : null;
  return (
    <div className={styles.contents}>
      <label className={styles.button}>
        {t('ui.settings.import')}
        <input
          type="file"
          accept="application/json,.json"
          className={styles.file}
          data-testid="save-import-file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            void file.text().then((text) => {
              setPicked(read(text));
            });
          }}
        />
      </label>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {picked?.ok && (
        <div className={styles.card} role="alertdialog" aria-label={t('ui.settings.import')}>
          <p>{t('ui.settings.import_summary', { ...picked.summary })}</p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                setPicked(null);
              }}
            >
              {t('ui.settings.reset_keep')}
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              data-testid="save-import-confirm"
              onClick={() => {
                setBusy(true);
                void apply(picked.save).then(() => {
                  window.location.reload();
                });
              }}
            >
              {t('ui.settings.import_go')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Bắt đầu lại" needs the player to type the phrase first (content-schemas §11.3), so a stray tap never wipes
 * a town. `onConfirm` does the work and reloads.
 */
export function ResetConfirm({
  body,
  onCancel,
  onConfirm,
}: {
  body: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useI18n();
  const inputId = useId();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const phrase = t('ui.settings.reset_phrase');
  const matches = normalize(typed) === normalize(phrase);
  return (
    <div className={styles.card} role="alertdialog" aria-label={t('ui.settings.reset')}>
      <p>{body}</p>
      <label className={styles.typeLabel} htmlFor={inputId}>
        {t('ui.settings.reset_type', { phrase })}
      </label>
      <input
        id={inputId}
        className={styles.input}
        value={typed}
        autoComplete="off"
        data-testid="reset-phrase"
        onChange={(e) => {
          setTyped(e.target.value);
        }}
      />
      <div className={styles.actions}>
        <button type="button" className={styles.button} onClick={onCancel}>
          {t('ui.settings.reset_keep')}
        </button>
        <button
          type="button"
          className={styles.danger}
          disabled={!matches || busy}
          data-testid="settings-reset-confirm"
          onClick={() => {
            setBusy(true);
            void onConfirm().then(() => {
              window.location.reload();
            });
          }}
        >
          {t('ui.settings.reset_go')}
        </button>
      </div>
    </div>
  );
}

const normalize = (s: string) => s.normalize('NFC').trim().toLocaleLowerCase('vi').replace(/\s+/g, ' ');
